import { Buildings, HourglassMedium, SignOut, UserCircle } from '@phosphor-icons/react';
import type { Me } from '@sm/api';
import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/Badge';
import { Card } from '@/components/ui/Card';
import { useTheme } from '@/hooks/usePreferences';

import { LanguageMenu, ThemeToggleButton } from './LanguageMenu';

/** Ekrani dostupni dok registracija firme čeka odobrenje (Podešavanja → Organizacija i Profil). */
export function allowedWhilePending(pathname: string, tab: string | undefined): boolean {
  if (pathname !== '/podesavanja') return false;
  return tab === undefined || tab === 'organizacija' || tab === 'profil';
}

/** „Registracija čeka odobrenje“: firma je registrovana, operater NENASAL još nije odobrio pristup. */
export function PendingApproval({ me }: { me: Me }) {
  const { t } = useTranslation();
  return (
    <Card
      pad="lg"
      className="mx-auto flex w-full max-w-[640px] flex-col items-center gap-4 py-10 text-center"
      data-testid="pending-approval"
    >
      <span className="grid h-14 w-14 place-items-center rounded-full bg-caution-bg text-caution">
        <HourglassMedium size={28} weight="duotone" />
      </span>
      <h1 className="m-0 font-heading text-[24px] font-semibold tracking-[-.01em]">
        {t('panel:pending.title')}
      </h1>
      <p className="m-0 max-w-[480px] text-sm leading-relaxed text-text2">
        {t('panel:pending.description', { company: me.tenant.name })}
      </p>
      <p className="m-0 max-w-[480px] text-[13px] leading-relaxed text-muted">
        {t('panel:pending.meanwhile')}
      </p>
      <div className="flex flex-wrap justify-center gap-2 pt-1">
        <Link
          to="/podesavanja"
          search={{ tab: 'organizacija' }}
          className="inline-flex min-h-10 items-center gap-2 rounded-md bg-btn px-4 text-sm font-semibold text-btn-fg hover:bg-btn-h hover:text-btn-fg"
          data-testid="pending-open-organization"
        >
          <Buildings size={16} />
          {t('panel:pending.openOrganization')}
        </Link>
        <Link
          to="/podesavanja"
          search={{ tab: 'profil' }}
          className="inline-flex min-h-10 items-center gap-2 rounded-md border border-line2 bg-surface px-4 text-sm font-semibold text-text hover:border-accent hover:text-accent"
        >
          <UserCircle size={16} />
          {t('panel:pending.openProfile')}
        </Link>
      </div>
      <p className="m-0 text-xs text-muted">{t('panel:pending.contact')}</p>
    </Card>
  );
}

/** Okvir panela dok firma čeka odobrenje: bez navigacije modula, samo podešavanja i odjava. */
export function PendingShell({
  me,
  onLogout,
  children,
}: {
  me: Me;
  onLogout: () => void;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const { theme, toggle } = useTheme(true);
  return (
    <div className="flex min-h-screen flex-col">
      <header className="no-print sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-line bg-surface px-5 sm:px-7">
        <Link to="/" className="flex items-center gap-3">
          <span className="font-heading text-xl leading-none font-semibold tracking-[-.02em] whitespace-nowrap text-text">
            Smart Measure
          </span>
        </Link>
        <span className="hidden min-w-0 truncate text-sm text-muted sm:inline">
          {me.tenant.name}
        </span>
        <Badge tone="caution" size="sm">
          {t('common:tenantStatus.pending_approval')}
        </Badge>
        <div className="ml-auto flex items-center gap-2">
          <LanguageMenu persistRemote />
          <ThemeToggleButton theme={theme} onToggle={toggle} />
          <button
            type="button"
            onClick={onLogout}
            aria-label={t('panel:shell.user.logout')}
            title={t('panel:shell.user.logout')}
            className="grid h-10 w-10 place-items-center rounded-md border border-line2 text-text hover:border-accent hover:text-accent"
            data-testid="logout"
          >
            <SignOut size={18} />
          </button>
        </div>
      </header>
      <main
        id="main"
        className="flex w-full max-w-[1100px] flex-col gap-6 self-center px-4 pt-7 pb-16 sm:px-7"
      >
        {children}
      </main>
    </div>
  );
}
